// services/adminMessaging.ts
// Admin Messaging service.
// User lookup queries accounts/ + profiles/ (flat index) per Phase 2 schema.
// Write operations (send response, send proactive) go through the sendAdminMessage
// callable CF which also writes an entry to adminActionLogs/.

import {
    collection,
    doc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    QueryConstraint,
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
} from 'firebase/storage';
import type {
    SendAdminMessageRequest,
    SendAdminMessageResponse,
} from '@omore/shared-types';
import { db, storage } from '../firebase/config';
import { callWithIdempotency } from '../firebase/callable';
import type {
    AdminIncomingMessage,
    AdminResponse,
    AdminMessageTemplate,
    MessagingAnalytics,
    AdminMessageQueueFilter,
    PaginatedMessagesResponse,
    SendResponseResult,
    CreateTemplateDTO,
} from '../types';

// ============================================
// FIRESTORE PATHS
// ============================================

const ADMIN_MESSAGES_PATH = 'adminMessages/incoming/items';
const ADMIN_TEMPLATES_PATH = 'adminMessageTemplates';

// ============================================
// HELPER FUNCTIONS
// ============================================

function timestampToDate(timestamp: any): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
    return new Date(timestamp);
}

async function uploadFile(file: File, path: string): Promise<string> {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
}

// ============================================
// MESSAGE QUEUE OPERATIONS
// ============================================

export async function fetchPendingMessages(
    filter: AdminMessageQueueFilter = {}
): Promise<PaginatedMessagesResponse> {
    try {
        const messagesRef = collection(db, ADMIN_MESSAGES_PATH);
        const constraints: QueryConstraint[] = [];

        const statusFilter = filter.status && filter.status.length > 0
            ? filter.status
            : ['pending'];

        if (statusFilter.length === 1) {
            constraints.push(where('status', '==', statusFilter[0]));
        }

        const sortField = filter.sortBy || 'sentAt';
        const sortDir = filter.sortDirection || 'desc';
        constraints.push(orderBy(sortField, sortDir));

        const pageSize = filter.pageSize || 50;
        constraints.push(limit(pageSize));

        const q = query(messagesRef, ...constraints);
        const snapshot = await getDocs(q);

        let messages: AdminIncomingMessage[] = snapshot.docs.map(d => {
            const data = d.data();
            return {
                id: d.id,
                fromAppleID: data.fromAppleID || '',
                fromProfileID: data.fromProfileID || '',
                fromExplorerName: data.fromExplorerName || 'Anonymous',
                message: data.message || '',
                sentAt: timestampToDate(data.sentAt),
                status: data.status || 'pending',
                moderationPassed: data.moderationPassed ?? true,
                moderationIssues: data.moderationIssues || [],
                responseId: data.responseId,
                readByAdminAt: data.readByAdminAt ? timestampToDate(data.readByAdminAt) : undefined,
                respondedAt: data.respondedAt ? timestampToDate(data.respondedAt) : undefined,
            };
        });

        if (filter.searchQuery) {
            const searchLower = filter.searchQuery.toLowerCase();
            messages = messages.filter(msg =>
                msg.message.toLowerCase().includes(searchLower) ||
                msg.fromExplorerName.toLowerCase().includes(searchLower)
            );
        }

        if (statusFilter.length > 1) {
            messages = messages.filter(msg => statusFilter.includes(msg.status));
        }

        return {
            messages,
            totalCount: messages.length,
            currentPage: filter.page || 0,
            totalPages: 1,
            hasMore: false,
        };
    } catch (error) {
        console.error('Error fetching messages:', error);
        throw new Error('Failed to fetch messages');
    }
}

export async function fetchMessageById(messageId: string): Promise<AdminIncomingMessage> {
    try {
        const messageRef = doc(db, ADMIN_MESSAGES_PATH, messageId);
        const snapshot = await getDoc(messageRef);

        if (!snapshot.exists()) {
            throw new Error('Message not found');
        }

        const data = snapshot.data();
        return {
            id: snapshot.id,
            fromAppleID: data.fromAppleID || '',
            fromProfileID: data.fromProfileID || '',
            fromExplorerName: data.fromExplorerName || 'Anonymous',
            message: data.message || '',
            sentAt: timestampToDate(data.sentAt),
            status: data.status || 'pending',
            moderationPassed: data.moderationPassed ?? true,
            moderationIssues: data.moderationIssues || [],
            responseId: data.responseId,
            readByAdminAt: data.readByAdminAt ? timestampToDate(data.readByAdminAt) : undefined,
            respondedAt: data.respondedAt ? timestampToDate(data.respondedAt) : undefined,
        };
    } catch (error) {
        console.error('Error fetching message:', error);
        throw new Error('Failed to fetch message');
    }
}

export async function getUnreadMessageCount(): Promise<number> {
    try {
        const messagesRef = collection(db, ADMIN_MESSAGES_PATH);
        const q = query(
            messagesRef,
            where('status', '==', 'pending'),
            where('readByAdminAt', '==', null)
        );
        const snapshot = await getDocs(q);
        return snapshot.size;
    } catch (error) {
        console.error('Error getting unread count:', error);
        return 0;
    }
}

// ============================================
// RESPONSE OPERATIONS (via callable CF)
// ============================================

/**
 * Send an admin response to a user message.
 * Delegates to the sendAdminMessage callable CF which:
 *   - uploads files (if any were pre-uploaded by the client to Storage)
 *   - writes to the user's inbox
 *   - updates the original message status
 *   - logs the action to adminActionLogs/
 */
export async function sendAdminResponse(
    messageId: string,
    response: {
        responseType: 'text' | 'voice' | 'image' | 'mixed';
        textContent?: string;
        voiceFile?: File;
        imageFile?: File;
        imageCaption?: string;
    }
): Promise<SendResponseResult> {
    try {
        const message = await fetchMessageById(messageId);

        // Upload files client-side first (Storage writes are client-SDK-only in this setup).
        let voiceUrl: string | undefined;
        let imageUrl: string | undefined;

        if (response.voiceFile) {
            const voicePath = `adminResponses/${messageId}/voice_${Date.now()}.m4a`;
            voiceUrl = await uploadFile(response.voiceFile, voicePath);
        }
        if (response.imageFile) {
            const imagePath = `adminResponses/${messageId}/image_${Date.now()}.jpg`;
            imageUrl = await uploadFile(response.imageFile, imagePath);
        }

        const data = await callWithIdempotency<SendAdminMessageRequest, SendAdminMessageResponse>(
            'sendAdminMessage',
            {
                messageType: 'response',
                inResponseToMessageId: messageId,
                toAppleID: message.fromAppleID,
                toProfileID: message.fromProfileID,
                toExplorerName: message.fromExplorerName,
                responseType: response.responseType,
                textContent: response.textContent,
                voiceUrl,
                imageUrl,
                imageCaption: response.imageCaption,
            }
        );

        return {
            success: data.success,
            responseId: data.messageId,
            message: 'Response sent successfully',
        };
    } catch (error) {
        console.error('Error sending response:', error);
        throw new Error('Failed to send response');
    }
}

export async function fetchResponseByMessage(message: AdminIncomingMessage): Promise<AdminResponse | null> {
    try {
        if (!message.responseId) return null;

        // Read from the legacy appleUsers path while iOS migrates to accounts/.
        const responsePath = `appleUsers/${message.fromAppleID}/profiles/${message.fromProfileID}/adminInbox/items/items/${message.responseId}`;
        const responseRef = doc(db, responsePath);
        const snapshot = await getDoc(responseRef);

        if (!snapshot.exists()) {
            console.warn('Response document not found:', responsePath);
            return null;
        }

        const data = snapshot.data();
        return {
            id: data.id || message.responseId,
            messageId: data.inResponseTo || message.id,
            toAppleID: message.fromAppleID,
            toProfileID: message.fromProfileID,
            toExplorerName: message.fromExplorerName,
            responseType: data.responseType,
            textContent: data.textContent,
            voiceURL: data.voiceURL || data.voiceUrl,
            imageURL: data.imageURL || data.imageUrl,
            imageCaption: data.imageCaption,
            sentByAdminEmail: data.sentByAdminEmail || 'look@omore.app',
            sentAt: timestampToDate(data.sentAt),
            deliveryStatus: data.deliveryStatus || 'delivered',
        };
    } catch (error) {
        console.error('Error fetching response:', error);
        return null;
    }
}

/**
 * Send a proactive (admin-initiated) message to a user.
 * Delegates to the sendAdminMessage callable CF for audit logging.
 */
export async function sendProactiveMessage(params: {
    toAppleID: string;
    toProfileID: string;
    toExplorerName: string;
    responseType: 'text' | 'voice' | 'image' | 'mixed';
    textContent?: string;
    voiceFile?: File;
    imageFile?: File;
    imageCaption?: string;
    sentByAdminEmail: string;
}): Promise<SendResponseResult> {
    try {
        const proactiveMessageId = `proactive_${Date.now()}`;

        let voiceUrl: string | undefined;
        let imageUrl: string | undefined;

        if (params.voiceFile) {
            const voicePath = `adminResponses/${proactiveMessageId}/voice_${Date.now()}.m4a`;
            voiceUrl = await uploadFile(params.voiceFile, voicePath);
        }
        if (params.imageFile) {
            const imagePath = `adminResponses/${proactiveMessageId}/image_${Date.now()}.jpg`;
            imageUrl = await uploadFile(params.imageFile, imagePath);
        }

        const data = await callWithIdempotency<SendAdminMessageRequest, SendAdminMessageResponse>(
            'sendAdminMessage',
            {
                messageType: 'proactive',
                toAppleID: params.toAppleID,
                toProfileID: params.toProfileID,
                toExplorerName: params.toExplorerName,
                responseType: params.responseType,
                textContent: params.textContent,
                voiceUrl,
                imageUrl,
                imageCaption: params.imageCaption,
            }
        );

        return {
            success: data.success,
            responseId: data.messageId,
            message: 'Proactive message sent successfully',
        };
    } catch (error) {
        console.error('Error sending proactive message:', error);
        throw new Error('Failed to send proactive message');
    }
}

/**
 * Search for users to send messages to.
 *
 * Phase 2: queries accounts/ + profiles/ (flat global index) as the primary source.
 * Falls back to message history for users who pre-date the schema migration.
 *
 * Returns a combined, deduplicated list sorted by message recency then name.
 */
export async function searchUsers(searchQuery: string = ''): Promise<Array<{
    appleID: string;       // kept for backward compat with delivery path; equals accountId for new accounts
    profileID: string;
    explorerName: string;
    lastMessageDate?: Date;
}>> {
    try {
        const usersMap = new Map<string, {
            appleID: string;
            profileID: string;
            explorerName: string;
            lastMessageDate?: Date;
        }>();

        // 1. Users who have previously messaged admin (highest relevance).
        try {
            const msgSnap = await getDocs(collection(db, ADMIN_MESSAGES_PATH));
            msgSnap.docs.forEach(d => {
                const data = d.data();
                const appleID = data.fromAppleID;
                const profileID = data.fromProfileID;
                const explorerName = data.fromExplorerName || 'Unknown';
                const sentAt = timestampToDate(data.sentAt);

                if (appleID && profileID) {
                    const key = `${appleID}_${profileID}`;
                    if (!usersMap.has(key) || sentAt > (usersMap.get(key)!.lastMessageDate || new Date(0))) {
                        usersMap.set(key, { appleID, profileID, explorerName, lastMessageDate: sentAt });
                    }
                }
            });
        } catch (err) {
            console.error('Error fetching users from messages:', err);
        }

        // 2. All registered profiles from the flat global index (accounts/ schema).
        // The profiles/ collection is the canonical source for Phase 2+.
        try {
            const profilesSnap = await getDocs(
                query(
                    collection(db, 'profiles'),
                    where('status', '==', 'active'),
                    limit(500)
                )
            );

            profilesSnap.docs.forEach(d => {
                const data = d.data();
                const profileId = d.id;
                const ownerAccountId = data.ownerAccountId || '';
                const explorerName = data.explorerName || 'Unknown User';
                // Use accountId as the appleID field during the transition; the
                // sendAdminMessage CF handles routing to the correct inbox path.
                const key = `${ownerAccountId}_${profileId}`;
                if (!usersMap.has(key)) {
                    usersMap.set(key, {
                        appleID: ownerAccountId,
                        profileID: profileId,
                        explorerName,
                    });
                }
            });
        } catch (err) {
            console.error('Error fetching from profiles index:', err);
        }

        let users = Array.from(usersMap.values());

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            users = users.filter(u =>
                u.explorerName.toLowerCase().includes(q) ||
                u.appleID.toLowerCase().includes(q) ||
                u.profileID.toLowerCase().includes(q)
            );
        }

        users.sort((a, b) => {
            if (a.lastMessageDate && b.lastMessageDate) {
                return b.lastMessageDate.getTime() - a.lastMessageDate.getTime();
            }
            if (a.lastMessageDate) return -1;
            if (b.lastMessageDate) return 1;
            return a.explorerName.localeCompare(b.explorerName);
        });

        return users;
    } catch (error) {
        console.error('Error searching users:', error);
        return [];
    }
}

// ============================================
// MESSAGE STATUS OPERATIONS
// ============================================

export async function markMessageAsRead(messageId: string): Promise<void> {
    try {
        const messageRef = doc(db, ADMIN_MESSAGES_PATH, messageId);
        await updateDoc(messageRef, { readByAdminAt: Timestamp.now() });
    } catch (error) {
        console.error('Error marking message as read:', error);
        throw new Error('Failed to mark message as read');
    }
}

export async function archiveMessage(messageId: string): Promise<void> {
    try {
        const messageRef = doc(db, ADMIN_MESSAGES_PATH, messageId);
        await updateDoc(messageRef, { status: 'archived' });
    } catch (error) {
        console.error('Error archiving message:', error);
        throw new Error('Failed to archive message');
    }
}

export async function unarchiveMessage(messageId: string): Promise<void> {
    try {
        const messageRef = doc(db, ADMIN_MESSAGES_PATH, messageId);
        await updateDoc(messageRef, { status: 'pending' });
    } catch (error) {
        console.error('Error unarchiving message:', error);
        throw new Error('Failed to unarchive message');
    }
}

// ============================================
// TEMPLATE OPERATIONS
// ============================================

export async function fetchMessageTemplates(): Promise<AdminMessageTemplate[]> {
    try {
        const templatesRef = collection(db, ADMIN_TEMPLATES_PATH);
        const snapshot = await getDocs(templatesRef);
        return snapshot.docs.map(d => {
            const data = d.data();
            return {
                id: d.id,
                name: data.name || '',
                responseType: data.responseType || 'text',
                category: data.category || 'custom',
                textContent: data.textContent,
                voiceUrl: data.voiceUrl,
                imageUrl: data.imageUrl,
                usageCount: data.usageCount || 0,
                createdAt: timestampToDate(data.createdAt),
                lastUsedAt: data.lastUsedAt ? timestampToDate(data.lastUsedAt) : undefined,
            };
        });
    } catch (error) {
        console.error('Error fetching templates:', error);
        throw new Error('Failed to fetch templates');
    }
}

export async function createMessageTemplate(dto: CreateTemplateDTO): Promise<{ templateId: string }> {
    try {
        // Templates are admin metadata, not user data; keep as direct writes.
        const { doc: firestoreDoc, setDoc } = await import('firebase/firestore');
        const templateId = `tmpl_${Date.now()}`;
        const templateRef = firestoreDoc(db, ADMIN_TEMPLATES_PATH, templateId);

        let voiceUrl: string | undefined;
        let imageUrl: string | undefined;

        if (dto.voiceFile) {
            const voicePath = `adminTemplates/${templateId}/voice.m4a`;
            voiceUrl = await uploadFile(dto.voiceFile, voicePath);
        }
        if (dto.imageFile) {
            const imagePath = `adminTemplates/${templateId}/image.jpg`;
            imageUrl = await uploadFile(dto.imageFile, imagePath);
        }

        const templateData: any = {
            name: dto.name,
            responseType: dto.responseType,
            category: dto.category,
            usageCount: 0,
            createdAt: Timestamp.now(),
        };
        if (dto.textContent) templateData.textContent = dto.textContent;
        if (voiceUrl) templateData.voiceUrl = voiceUrl;
        if (imageUrl) templateData.imageUrl = imageUrl;

        await setDoc(templateRef, templateData);
        return { templateId };
    } catch (error) {
        console.error('Error creating template:', error);
        throw new Error('Failed to create template');
    }
}

export async function deleteMessageTemplate(templateId: string): Promise<void> {
    try {
        const { doc: firestoreDoc, deleteDoc } = await import('firebase/firestore');
        await deleteDoc(firestoreDoc(db, ADMIN_TEMPLATES_PATH, templateId));
    } catch (error) {
        console.error('Error deleting template:', error);
        throw new Error('Failed to delete template');
    }
}

export async function trackTemplateUsage(templateId: string): Promise<void> {
    try {
        const templateRef = doc(db, ADMIN_TEMPLATES_PATH, templateId);
        const snapshot = await getDoc(templateRef);
        if (snapshot.exists()) {
            await updateDoc(templateRef, {
                usageCount: (snapshot.data().usageCount || 0) + 1,
                lastUsedAt: Timestamp.now(),
            });
        }
    } catch (error) {
        console.error('Error tracking template usage:', error);
    }
}

// ============================================
// ANALYTICS
// ============================================

export async function getMessagingAnalytics(
    startDate: Date,
    endDate: Date
): Promise<MessagingAnalytics> {
    try {
        const snapshot = await getDocs(collection(db, ADMIN_MESSAGES_PATH));
        const messages = snapshot.docs.map(d => {
            const data = d.data();
            return {
                sentAt: timestampToDate(data.sentAt),
                status: data.status,
                respondedAt: data.respondedAt ? timestampToDate(data.respondedAt) : undefined,
                responseType: data.responseType,
                moderationPassed: data.moderationPassed ?? true,
            };
        });

        const filtered = messages.filter(
            msg => msg.sentAt >= startDate && msg.sentAt <= endDate
        );

        const messagesReceived = filtered.length;
        const responsesSent = filtered.filter(m => m.status === 'responded').length;
        const responseRate = messagesReceived > 0 ? (responsesSent / messagesReceived) * 100 : 0;

        const responseTimes = filtered
            .filter(m => m.respondedAt)
            .map(m => (m.respondedAt!.getTime() - m.sentAt.getTime()) / (1000 * 60));
        const avgResponseTime =
            responseTimes.length > 0
                ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
                : 0;

        const responsesByType = { text: 0, voice: 0, image: 0, mixed: 0 };
        filtered.forEach(m => {
            if (m.responseType && m.status === 'responded') {
                responsesByType[m.responseType as keyof typeof responsesByType]++;
            }
        });

        return {
            messagesReceived,
            responsesSent,
            responseRate,
            avgResponseTime,
            responsesByType,
            moderationFlags: filtered.filter(m => !m.moderationPassed).length,
        };
    } catch (error) {
        console.error('Error fetching analytics:', error);
        throw new Error('Failed to fetch analytics');
    }
}

export async function getMessagingQuickStats(): Promise<{
    pendingMessages: number;
    totalMessages: number;
    totalResponses: number;
    avgResponseTimeMinutes: number;
}> {
    try {
        const snapshot = await getDocs(collection(db, ADMIN_MESSAGES_PATH));
        const messages = snapshot.docs.map(d => d.data());
        return {
            pendingMessages: messages.filter(m => m.status === 'pending').length,
            totalMessages: messages.length,
            totalResponses: messages.filter(m => m.status === 'responded').length,
            avgResponseTimeMinutes: 0,
        };
    } catch (error) {
        console.error('Error fetching quick stats:', error);
        return { pendingMessages: 0, totalMessages: 0, totalResponses: 0, avgResponseTimeMinutes: 0 };
    }
}

// ============================================
// REAL-TIME SUBSCRIPTIONS (polling)
// ============================================

export function subscribeToPendingMessages(
    callback: (messages: AdminIncomingMessage[]) => void,
    pollIntervalMs: number = 30000
): () => void {
    let intervalId: NodeJS.Timeout;
    const poll = async () => {
        try {
            const result = await fetchPendingMessages({ status: ['pending'], pageSize: 100 });
            callback(result.messages);
        } catch (error) {
            console.error('Error polling pending messages:', error);
        }
    };
    poll();
    intervalId = setInterval(poll, pollIntervalMs);
    return () => { if (intervalId) clearInterval(intervalId); };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

export function validateResponse(response: {
    responseType: 'text' | 'voice' | 'image' | 'mixed';
    textContent?: string;
    voiceFile?: File;
    imageFile?: File;
}): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    switch (response.responseType) {
        case 'text':
            if (!response.textContent || !response.textContent.trim()) errors.push('Text content is required');
            if (response.textContent && response.textContent.length > 5000) errors.push('Text must be < 5000 chars');
            break;
        case 'voice':
            if (!response.voiceFile) errors.push('Voice file is required');
            if (response.voiceFile && response.voiceFile.size > 10 * 1024 * 1024) errors.push('Voice file must be < 10MB');
            break;
        case 'image':
            if (!response.imageFile) errors.push('Image file is required');
            if (response.imageFile && response.imageFile.size > 5 * 1024 * 1024) errors.push('Image must be < 5MB');
            break;
        case 'mixed':
            if (!response.textContent && !response.voiceFile && !response.imageFile)
                errors.push('At least one content type is required');
            break;
    }
    return { isValid: errors.length === 0, errors };
}

export function formatRelativeTime(date: Date): string {
    const diff = Date.now() - new Date(date).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 7) return new Date(date).toLocaleDateString();
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
}

export function getFilePreviewUrl(file: File): string { return URL.createObjectURL(file); }
export function revokePreviewUrl(url: string): void { URL.revokeObjectURL(url); }
